import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../lib/api-client';
import { Badge } from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { useActivityHeartbeat } from '../../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../../hooks/useCacheEvents';
import { useArchivePanel, ArchiveHeader } from './ArchivePanel';

interface ActivityEntry {
  id: string;
  timestamp: string;
  streamId: string;
  runId?: string;
  eventType: string;
  description: string;
  actor: string;
}

interface ActivityLogTabProps {
  streamId: string;
}

function formatTs(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function eventVariant(
  type: string,
): 'default' | 'success' | 'destructive' | 'outline' {
  if (type === 'failed' || type === 'escalated') return 'destructive';
  if (type === 'approved' || type === 'published' || type === 'agent_completed') return 'success';
  return 'outline';
}

export function ActivityLogTab({ streamId }: ActivityLogTabProps) {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archiveEntries, setArchiveEntries] = useState<ActivityEntry[]>([]);

  useActivityHeartbeat('cache:ai-activity', true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (streamId) params.set('streamId', streamId);
      const qs = params.toString();
      const path = qs ? `/api/ai-workflows/activity?${qs}` : '/api/ai-workflows/activity';
      const data = await apiClient.get<{ entries: ActivityEntry[]; total: number }>(path);
      setEntries(data.entries || []);
    } catch (e: unknown) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Failed to load activity');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    void load();
  }, [load]);

  useCacheEvents({
    cacheNames: ['ai-activity'],
    onEvent: () => {
      void load();
    },
  });

  const archivePanel = useArchivePanel({
    onSearch: async (from, to) => {
      const result = await apiClient.getAiActivityArchive({
        from,
        to,
        streamId: streamId || undefined,
      });
      setArchiveEntries(result.entries || []);
    },
  });

  const displayEntries = archivePanel.archiveMode ? archiveEntries : entries;

  return (
    <div className="space-y-4">
      <ArchiveHeader label="Activity" count={displayEntries.length} panel={archivePanel} />

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      ) : displayEntries.length === 0 ? (
        <Card className="border-dashed border-gray-300 dark:border-gray-600">
          <CardContent className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
            {archivePanel.archiveMode ? 'No activity entries found in this date range.' : 'No activity for this stream filter yet.'}
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2">
          {displayEntries.map((entry) => (
            <li key={entry.id}>
              <Card className="border-gray-200 dark:border-gray-700">
                <CardContent className="flex flex-col gap-2 py-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {formatTs(entry.timestamp)}
                      </span>
                      <Badge variant={eventVariant(entry.eventType)}>{entry.eventType}</Badge>
                      <Badge variant="outline" className="font-normal">
                        {entry.streamId}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{entry.description}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium text-gray-600 dark:text-gray-300">Actor </span>
                      {entry.actor}
                      {entry.runId ? (
                        <>
                          <span className="mx-1 text-gray-300 dark:text-gray-600">·</span>
                          <span className="font-mono">{entry.runId}</span>
                        </>
                      ) : null}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
